import React, { useState, useEffect } from "react";
import { Column, Row, Sheet, Text, Button, Icon, Loader } from "@amzn/meridian";
import chevronLeftLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import chevronRightLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-right-large";
import { ObjectPresenceCheck, Evidence } from "src/services/inspectionService";
import "./ComplianceDetailSheet.scss";
import ThemedTag from "./ThemedTag";

interface ObjectPresenceDetailSheetProps {
    open: boolean;
    onClose: () => void;
    check: ObjectPresenceCheck | null;
    onOverride: (check: ObjectPresenceCheck) => void;
    isAnalysisPending?: boolean;
    evidence: Evidence[];
    presignedUrlMap: Record<string, string>;
}

const ObjectPresenceDetailSheet: React.FC<ObjectPresenceDetailSheetProps> = ({
    open,
    onClose,
    check,
    onOverride,
    isAnalysisPending = false,
    evidence,
    presignedUrlMap,
}) => {
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        setActiveIndex(0);
    }, [check, open]);

    if (!check) {
        return null;
    }

    const evidenceUrls = evidence
        .map((e) => presignedUrlMap[e.uri])
        .filter(Boolean);

    return (
        <Sheet type="overlay" side="bottom" open={open} onClose={onClose} className="compliance-detail-sheet">
            <Column spacing="400" spacingInset="500">
                <Text type="h300">Compliance Details</Text>
                <Row alignmentHorizontal="justify" alignmentVertical="top">
                    <Column spacing="100">
                        <Text type="b100" color="secondary">Object</Text>
                        <Text type="b200" color="primary">{check.object_name}</Text>
                    </Column>
                    <ThemedTag
                        text={isAnalysisPending ? "Analyzing..." : check.is_compliant ? "Compliant" : "Non Compliant"}
                        type={isAnalysisPending ? "neutral" : check.is_compliant ? "success" : "error"}
                    />
                </Row>

                {isAnalysisPending ? (
                    <Loader type="circular" size="large" />
                ) : (
                    <>
                        <Column spacing="100">
                            <Text type="b100" color="secondary">Answer</Text>
                            <Text type="b200" color="primary">{check.is_compliant ? "YES" : "NO"}</Text>
                        </Column>

                        {check.ai_reason && (
                            <Column spacing="100">
                                <Text type="b100" color="secondary">Reason</Text>
                                <Text type="b200" color="primary">{check.ai_reason}</Text>
                            </Column>
                        )}

                        {evidenceUrls.length > 0 && (
                    <Column spacing="300">
                        <Text type="b100" color="secondary">Evidence</Text>
                        <Row alignmentHorizontal="justify"  width="100%">
                            {evidenceUrls.length > 1 && (
                                <Button type="icon" onClick={() => setActiveIndex(activeIndex - 1)} disabled={activeIndex <= 0}>
                                    <Icon tokens={chevronLeftLargeTokens} color={activeIndex <= 0 ? "secondary" : "primary"} />
                                </Button>
                            )}
                            <img
                                src={evidenceUrls[activeIndex]}
                                alt={`Evidence ${activeIndex + 1}`}
                                className="compliance-detail-sheet__image"
                            />
                            {evidenceUrls.length > 1 && (
                                <Button type="icon" onClick={() => setActiveIndex(activeIndex + 1)} disabled={activeIndex >= evidenceUrls.length - 1}>
                                    <Icon tokens={chevronRightLargeTokens} color={activeIndex >= evidenceUrls.length - 1 ? "secondary" : "primary"} />
                                </Button>
                            )}
                        </Row>
                        {evidenceUrls.length > 1 && (
                            <Text type="b200" color="secondary" alignment="center">
                                Image {activeIndex + 1} of {evidenceUrls.length}
                            </Text>
                        )}
                    </Column>
                )}

                        {!isAnalysisPending && (
                            <button
                                className="compliance-detail-sheet__override-btn"
                                onClick={() => onOverride(check)}
                            >
                                MANUAL OVERRIDE
                            </button>
                        )}
                    </>
                )}
            </Column>
        </Sheet>
    );
};

export default ObjectPresenceDetailSheet;
